import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TenantService } from '../../services/tenant.service';
import { ModuleService } from '../../services/module.service';
import { ToastService } from '../../services/toast.service';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { Tenant } from '../../models/tenant.model';
import { Module } from '../../models/module.model';
import { MaterialModule } from '../../shared/material.module';
import { fadeInUpAnimation } from '../../shared/animations/fade-in.animation';
import { listStaggerAnimation } from '../../shared/animations/stagger.animation';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, MaterialModule, RouterModule],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  animations: [fadeInUpAnimation, listStaggerAnimation]
})
export class DashboardComponent implements OnInit {
  // Stats
  totalTenants = 0;
  totalModules = 0;
  activeTenants = 0;
  coreModules = 0;
  recentTenants: Tenant[] = [];
  recentModules: Module[] = [];
  isLoading = true;
  isLoadingTenants = true;
  isLoadingModules = true;
  
  // Animated counters
  displayTotalTenants = 0;
  displayTotalModules = 0;
  displayActiveTenants = 0;
  displayCoreModules = 0;

  constructor(
    private tenantService: TenantService,
    private moduleService: ModuleService,
    private toastService: ToastService,
    private errorHandler: ErrorHandlerService
  ) {}

  ngOnInit(): void {
    this.loadDashboardData();
  }

  private loadDashboardData(): void {
    this.isLoading = true;
    this.isLoadingTenants = true;
    this.isLoadingModules = true;
    
    this.tenantService.getAllTenants().subscribe({
      next: (tenants) => {
        this.totalTenants = tenants.length;
        this.activeTenants = tenants.filter(t => t.isActive).length;
        this.recentTenants = tenants.slice(0, 5);
        
        // Animate counters
        this.animateCounter('totalTenants', this.totalTenants);
        this.animateCounter('activeTenants', this.activeTenants);
        
        this.isLoadingTenants = false;
        this.isLoading = false;
      },
      error: (error) => {
        const errorResult = this.errorHandler.handleError(error);
        this.toastService.error('Error', errorResult.message);
        this.isLoadingTenants = false;
        this.isLoading = false;
      }
    });

    this.moduleService.getAllModules().subscribe({
      next: (modules) => {
        this.totalModules = modules.length;
        this.coreModules = modules.filter(m => m.isCore).length;
        this.recentModules = modules.slice(0, 5);
        
        // Animate counters
        this.animateCounter('totalModules', this.totalModules);
        this.animateCounter('coreModules', this.coreModules);
        
        this.isLoadingModules = false;
      },
      error: (error) => {
        const errorResult = this.errorHandler.handleError(error);
        this.toastService.error('Error', errorResult.message);
        this.isLoadingModules = false;
      }
    });
  }
  
  private animateCounter(property: string, target: number): void {
    const duration = 1000;
    const steps = 30;
    const increment = target / steps;
    let current = 0;
    const interval = duration / steps;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        current = target;
        clearInterval(timer);
      }
      
      switch(property) {
        case 'totalTenants':
          this.displayTotalTenants = Math.floor(current);
          break;
        case 'totalModules':
          this.displayTotalModules = Math.floor(current);
          break;
        case 'activeTenants':
          this.displayActiveTenants = Math.floor(current);
          break;
        case 'coreModules':
          this.displayCoreModules = Math.floor(current);
          break;
      }
    }, interval);
  }
  
  getStatusBadgeClass(isActive: boolean): string {
    return isActive ? 'status-badge-active' : 'status-badge-inactive';
  }
  
  getModuleTypeClass(type: string | undefined): string {
    if (!type) return 'type-other';
    return `type-${type.toLowerCase().replace(/\s+/g, '-')}`;
  }
}